import { Link, NavLink, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { useAuth } from "../lib/auth";
import { useSettings } from "../lib/settings";
import { useCart } from "../lib/cart";
import { api } from "../lib/api";
import { ShoppingCart, User, Heart, Search, X } from "lucide-react";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/shop", label: "Shop" },
  { to: "/shop?category=ai-prompts", label: "Prompts" },
  { to: "/shop?category=media", label: "Media" },
  { to: "/about", label: "About" },
];

export default function Header() {
  const { user } = useAuth();
  const { settings } = useSettings();
  const cart = useCart();
  const navigate = useNavigate();
  const [wishCount, setWishCount] = useState(0);
  const [search, setSearch] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const searchInputRef = useRef(null);

  function submitSearch(e) {
    e.preventDefault();
    const q = (search || "").trim();
    setSearchOpen(false);
    if (q) navigate(`/shop?q=${encodeURIComponent(q)}`);
    else navigate("/shop");
  }

  function toggleSearch() {
    setSearchOpen((v) => {
      const next = !v;
      if (next) setTimeout(() => searchInputRef.current?.focus(), 50);
      else setSearch("");
      return next;
    });
  }

  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") { setSearchOpen(false); setSearch(""); }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    let cancelled = false;
    let email = "";
    try { email = localStorage.getItem("ttfm_wishlist_email") || ""; } catch { /* ignore */ }
    if (!email) { setWishCount(0); return; }
    api.get(`/wishlist/by-email?email=${encodeURIComponent(email)}`)
      .then((r) => { if (!cancelled) setWishCount((r.data || []).length); })
      .catch(() => { /* ignore */ });
    return () => { cancelled = true; };
  }, []);

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-50 backdrop-blur-xl bg-[#FDFBF7]/85 border-b-2 border-[var(--accent)]/40"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex flex-col items-center gap-3">
        {/* Top row: centered brand stack + action icons on the right */}
        <div className="w-full flex items-start justify-between">
          <div className="w-32" />
          <Link to="/" data-testid="logo-link" className="flex flex-col items-center gap-2 min-w-0">
            <div className="font-serif text-2xl sm:text-3xl lg:text-4xl tracking-[0.18em] uppercase text-[#1A1815] font-semibold leading-tight text-center">
              {settings.brand_name}
            </div>
            <img
              src={settings.logo_url || "/logo.png"}
              alt={settings.brand_name}
              data-testid="brand-logo-icon"
              className="h-20 sm:h-24 lg:h-28 w-auto object-contain shrink-0"
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
            <div className="font-serif italic gold-heading text-base sm:text-lg tracking-wider leading-none text-center">
              {settings.tagline}
            </div>
          </Link>
          <div className="w-32 flex justify-end items-center gap-2">
            <button
              onClick={toggleSearch}
              data-testid="header-search-toggle"
              aria-label="Search"
              aria-expanded={searchOpen}
              className="inline-flex items-center justify-center w-11 h-11 rounded-full border-2 border-[var(--accent)]/60 hover:border-[var(--accent)] hover:bg-[#F5F2EA] transition"
            >
              {searchOpen ? <X className="w-5 h-5 text-[#2C2924]" /> : <Search className="w-5 h-5 text-[#2C2924]" />}
            </button>
            <Link to="/account" data-testid="account-link"
              className="hidden sm:inline-flex items-center justify-center w-11 h-11 rounded-full border-2 border-[var(--accent)]/60 hover:border-[var(--accent)] hover:bg-[#F5F2EA] transition"
              aria-label="My account">
              <User className="w-5 h-5 text-[#2C2924]" />
            </Link>
            <Link to="/wishlist" data-testid="wishlist-link"
              className="relative inline-flex items-center justify-center w-11 h-11 rounded-full border-2 border-[var(--accent)]/60 hover:border-[var(--accent)] hover:bg-[#F5F2EA] transition"
              aria-label="Open wishlist"
            >
              <Heart className="w-5 h-5 text-[#2C2924]" />
              {wishCount > 0 && (
                <span data-testid="wishlist-count"
                  className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[var(--accent)] text-white text-[10px] font-semibold flex items-center justify-center">
                  {wishCount}
                </span>
              )}
            </Link>
            <button
              onClick={() => cart.setOpen(true)}
              data-testid="cart-button"
              className="relative inline-flex items-center justify-center w-11 h-11 rounded-full border-2 border-[var(--accent)]/60 hover:border-[var(--accent)] hover:bg-[#F5F2EA] transition"
              aria-label="Open cart"
            >
              <ShoppingCart className="w-5 h-5 text-[#2C2924]" />
              {cart.count > 0 && (
                <span data-testid="cart-count"
                  className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[var(--accent)] text-white text-[10px] font-semibold flex items-center justify-center">
                  {cart.count}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Search dropdown — slides in below the brand when toggled */}
        {searchOpen && (
          <form
            onSubmit={submitSearch}
            className="w-full max-w-2xl mx-auto animate-in fade-in slide-in-from-top-2 duration-150"
            data-testid="header-search-form"
          >
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C5547]" />
              <input
                ref={searchInputRef}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                type="search"
                placeholder="Search jewelry, prompts, ebooks…"
                data-testid="header-search-input"
                className="w-full pl-11 pr-4 py-2.5 rounded-full bg-[#F5F2EA] border border-[#EAE4D3] focus:outline-none focus:ring-2 focus:ring-[#D4AF37] text-sm placeholder:text-[#5C5547]"
              />
            </div>
          </form>
        )}

        {/* Bottom row: gold bold nav */}
        <nav className="w-full flex flex-wrap items-center justify-center gap-x-8 gap-y-2 pt-2 border-t border-[#EAE4D3]">
          {NAV.map((l) => (
            <NavLink
              key={l.label}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `font-serif uppercase tracking-[0.25em] text-sm font-bold transition-all hover:scale-105 ${
                  isActive
                    ? "gold-heading"
                    : "text-[var(--accent)] hover:text-[#B8860B]"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <Link
            to={user && user.role === "admin" ? "/admin" : "/admin/login"}
            data-testid="nav-admin"
            className="font-serif uppercase tracking-[0.25em] text-sm font-bold text-[var(--accent)] hover:text-[#B8860B] hover:scale-105 transition-all inline-flex items-center gap-1"
          >
            <User className="w-3.5 h-3.5" /> Admin
          </Link>
        </nav>
      </div>
    </header>
  );
}
